import { useEffect, useRef } from 'react';
import type { GoogleAnalyticsEventValue } from '@elfsight/lib-google-analytics';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import type {
  GoogleAnalyticsParameterType,
  GoogleAnalyticsValueType
} from '../../../types';

const POST_VIEW_STORAGE_KEY_PREFIX = 'feeds.post-viewed';

const getViewedPosts = (storageKey: string): string[] => {
  try {
    const stored = sessionStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const markPostAsViewed = (storageKey: string, viewStateKey: string) => {
  try {
    const viewed = getViewedPosts(storageKey);
    viewed.push(viewStateKey);
    sessionStorage.setItem(storageKey, JSON.stringify(viewed));
  } catch {
    return;
  }
};

interface UsePostViewTrackingParams {
  visibility: boolean;
  isInPopup: boolean;
  widgetId: string;
  postId: string;
  postUrl: string;
  editMode?: boolean;
  googleAnalytics: GoogleAnalyticsEventValue<
    GoogleAnalyticsValueType,
    GoogleAnalyticsParameterType
  >[];
}

export function usePostViewTracking({
  visibility,
  isInPopup,
  widgetId,
  postId,
  postUrl,
  editMode,
  googleAnalytics
}: UsePostViewTrackingParams) {
  const viewTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const viewContext = isInPopup ? 'popup' : 'feed';
    const storageKey = `${POST_VIEW_STORAGE_KEY_PREFIX}.${widgetId}`;
    const viewStateKey = `${postId}.${viewContext}`;

    if (!visibility || getViewedPosts(storageKey).includes(viewStateKey)) {
      return;
    }

    if (viewTimerRef.current) {
      clearTimeout(viewTimerRef.current);
    }

    viewTimerRef.current = setTimeout(() => {
      viewTimerRef.current = null;

      sendGoogleAnalyticsEvents({
        eventType: 'postViewed',
        googleAnalyticsEvents: googleAnalytics,
        editMode,
        valueMapping: {
          widgetId,
          postId,
          postUrl,
          viewContext
        }
      });

      markPostAsViewed(storageKey, viewStateKey);
    }, 1000);

    return () => {
      if (viewTimerRef.current) {
        clearTimeout(viewTimerRef.current);
        viewTimerRef.current = null;
      }
    };
  }, [
    visibility,
    isInPopup,
    widgetId,
    postId,
    postUrl,
    editMode,
    googleAnalytics
  ]);
}
